/**
 * CORE-1123 — редакционная часть `GET /api/home`, общая для веба и приложения.
 *
 * Главную страницу редактируют в админке (CORE-593): тексты, подписи, CTA,
 * ссылки и тумблеры видимости блоков. Агрегатор (CORE-592) отдаёт это в
 * `content.blocks` — словаре, где ключ блока постоянен, а всё остальное может
 * меняться без релиза.
 *
 * Читатели у словаря теперь двое, поэтому правила чтения живут здесь, а не в
 * экране. Смысл ровно в этом: одна опубликованная версия — одинаково понятая
 * на обеих платформах. Разъехавшиеся копии этих четырёх функций означали бы,
 * что «выключено» на вебе и «выключено» в приложении — разные вещи, и узнать
 * об этом можно было бы только от пользователя.
 *
 * Здесь только чтение словаря: ни запросов, ни навигации, ни вёрстки. Ссылки
 * разбирает {@link ./homeLinkTarget}, кэш последней публикации —
 * {@link ./homeFeedCache}.
 */

import type {
  HomeAppLayout,
  HomeContentBlock,
  HomeContentCard,
  HomeFeedResponse,
} from '../../types/auth';

/** Блок по ключу, либо null — блока в публикации нет. */
export function homeBlock(
  feed: HomeFeedResponse | null | undefined,
  key: string,
): HomeContentBlock | null {
  return feed?.content?.blocks?.[key] ?? null;
}

/**
 * Виден ли блок.
 *
 * Отсутствующий блок считается ВКЛЮЧЁННЫМ, и это не небрежность: пока
 * редактор не тронул блок, его в словаре нет, а страница должна выглядеть
 * так же, как выглядела. Скрывает только явное `enabled: false` — то есть
 * осознанное решение редактора, а не пробел в данных или сбой сети.
 */
export function isHomeBlockEnabled(
  feed: HomeFeedResponse | null | undefined,
  key: string,
): boolean {
  const b = homeBlock(feed, key);
  return b ? b.enabled !== false : true;
}

/**
 * Флаг фида (`featureFlags` внутри ответа `/api/home`) — отдельная от
 * `/api/features` история: он про то, отдавать ли раздел на этой главной.
 * Неизвестный флаг — «да», по той же причине, что и у блоков.
 */
export function isHomeFeatureEnabled(
  feed: HomeFeedResponse | null | undefined,
  featureKey: string,
): boolean {
  const flags = feed?.featureFlags;
  if (!flags || !(featureKey in flags)) return true;
  return flags[featureKey] === true;
}

/** Карточки блока, без пустых (ни заголовка, ни текста — рисовать нечего). */
export function homeBlockCards(
  blockData: HomeContentBlock | null | undefined,
): HomeContentCard[] {
  return blockData?.cards?.filter((card) => card.title || card.text) || [];
}

/** Заголовок/подпись/CTA секции — то, чем админка управляет в шапке блока. */
export interface HomeSectionCopy {
  /** false — редактор выключил блок, секцию рисовать не надо. */
  enabled: boolean;
  title: string;
  subtitle: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

/**
 * Собирает шапку секции из блока, подставляя локальные тексты там, где
 * админка молчит.
 *
 * Фолбэк не «на всякий случай», а по требованию: приложение обязано остаться
 * рабочим и непустым, даже если `/api/home` недоступен или блок пуст. Пустая
 * строка от редактора при этом НЕ считается ответом — иначе случайно стёртое
 * поле оставило бы секцию без заголовка.
 */
export function resolveHomeSectionCopy(
  feed: HomeFeedResponse | null | undefined,
  key: string,
  fallback: { title: string; subtitle?: string | null },
): HomeSectionCopy {
  const b = homeBlock(feed, key);
  const cta = b?.cta?.find((c) => c.label || c.url) ?? null;
  const trimmed = (value: string | null | undefined): string | null => {
    const v = value?.trim();
    return v ? v : null;
  };

  return {
    enabled: b ? b.enabled !== false : true,
    title: trimmed(b?.title) ?? fallback.title,
    subtitle: trimmed(b?.subtitle) ?? (fallback.subtitle ?? null),
    ctaLabel: trimmed(cta?.label),
    ctaUrl: trimmed(cta?.url),
  };
}

/**
 * CORE-1866 — как приложение раскладывает списочный раздел главной: список или
 * карусель, по выбору в админке («Отображение в приложении»).
 *
 * Не выбрано, неизвестное значение (новая раскладка со старым клиентом), нет
 * блока или нет ответа `/api/home` — `fallback`, то есть текущая раскладка
 * раздела: старый клиент и пустая админка выглядят как раньше. Только для
 * нативной главной — веб это поле не читает.
 */
export function resolveHomeSectionLayout(
  feed: HomeFeedResponse | null | undefined,
  key: string,
  fallback: HomeAppLayout,
): HomeAppLayout {
  const v = homeBlock(feed, key)?.appLayout;
  return v === 'list' || v === 'carousel' ? v : fallback;
}
